import { SELECTED_SERVICE_DELIMITER, TeaserImage } from '../models';

export class UrlUtils {
  private static readonly ACCEPT_DOMAINS = [
    'beprobooking.com',
    'bprobooking.com',
    'bepro.me',
    // custom website designed by Lumxi GmbH
    'beautynails-basel.ch',
    'beautynails-letzipark.ch',
    'beautynails-volkiland.ch',
    'bella-nailslashes.ch',
    'calibeautykassel.de',
    'glossy-beauty.ch',
    'heynails.ch',
    'lc-beauty.info',
    'lena-spa.de',
    'nails-center.ch',
    'nina-beauty-salon.de',
    'venus-nails-swiss.ch',
  ];

  public static addParameterToUrl(
    originalUrl: string,
    parameterOption: any
  ): string {
    let parameterPath = '';

    Object.keys(parameterOption).forEach((key) => {
      if (key && parameterOption[key]) {
        parameterPath += parameterPath !== '' ? '&' : '';
        parameterPath += `${key}=${encodeURIComponent(parameterOption[key])}`;
      }
    });

    return parameterPath !== ''
      ? `${originalUrl}?${parameterPath}`
      : originalUrl;
  }

  public static getCurrentUrlWithoutQueryParameter(): string {
    return `${window.location.origin}${window.location.pathname}`;
  }

  public static createGoogleMapUrl(...args): string {
    let url = 'https://www.google.com/maps/search/?api=1&query=';

    args?.forEach((a, index) => {
      if (index) {
        // add space to separate
        url = `${url}${encodeURIComponent(' ')}`;
      }
      url = `${url}${encodeURIComponent(a)}`;
    });

    return url;
  }

  public static createLinkToGoogleMapReviewsPage(placeId: string): string {
    if (!placeId) {
      return null;
    }
    return `https://search.google.com/local/reviews?placeid=${placeId}`;
  }

  public static getPathToTeaserImage(
    domain: string,
    teaserImage: TeaserImage
  ): string {
    return `${domain}/images/teasers/${teaserImage?.path}`;
  }

  public static parseStaffIdFromString(staffIdStr: string): number {
    return staffIdStr?.length ? Number(staffIdStr) : null;
  }

  public static parseProductIdsFromString(productIdsStr: string): number[] {
    if (!productIdsStr?.length) {
      return [];
    }

    return [
      ...new Set<number>(
        productIdsStr
          ?.split(SELECTED_SERVICE_DELIMITER)
          ?.map((pId) => {
            try {
              return Number(pId);
            } catch (e) {
              return null;
            }
          })
          ?.filter((pId) => pId !== null) || []
      ),
    ];
  }

  public static getUnsupportedUrlsFromText(str: string): string[] {
    if (!str?.length) {
      return [];
    }

    const urls = UrlUtils.getUrlsFromString(str);
    return urls?.filter(
      (url) =>
        !UrlUtils.isSuspectedUrlGermanDate(url) &&
        !UrlUtils.ACCEPT_DOMAINS?.find((domain) =>
          this.isUrlMatchDomain(url, domain)
        )
    );
  }

  private static isSuspectedUrlGermanDate(url: string): boolean {
    // in German date will be displayed as 01.12 (1-st December)
    const regexToCheck = new RegExp('[0-9]+\\.[0-9]+', 'g');
    return regexToCheck.test(url);
  }

  private static getUrlsFromString(str: string): string[] {
    const regexToCheck = new RegExp(
      '((ftp|http|https|gopher|mailto|news|nntp|telnet|wais|file|prospero|aim|webcal):\\/\\/)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_\\+.~#?&\\/=]*)?',
      'g'
    );

    return str.match(regexToCheck) || [];
  }

  private static isUrlMatchDomain(url: string, domain: string): boolean {
    const domainHttp = `http://${domain}`;
    const domainHttps = `https://${domain}`;
    const domainWww = `www.${domain}`;

    return (
      this.isUrlStartWithHost(url, domain) ||
      this.isUrlStartWithHost(url, domainHttp) ||
      this.isUrlStartWithHost(url, domainHttps) ||
      this.isUrlStartWithHost(url, domainWww)
    );
  }

  private static isUrlStartWithHost(url: string, host: string): boolean {
    return url === host || url.startsWith(`${host}/`);
  }
}
